import { useCallback, useEffect, useState } from 'react'
import { errorMessage, getReportSummary } from '../../services/admin'
import { formatDayCompact } from './dates'
import { dateInputToIso, defaultRange, isoToDateInput, numberFa, percentFa } from './reportRange'

// ─── Reports tab (admin-reports, S3-p) ────────────────────────────────────────

const inputCls = 'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none'

function Card({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-800">{value}</div>
      {hint && <div className="text-xs text-slate-400">{hint}</div>}
    </div>
  )
}

/**
 * The summary, given `admin-reports_summary`'s answer: the range's totals as
 * cards, one row per day, one row per Saturday-start week with activity.
 * Dates are the wire's Tehran calendar days, shown Gregorian.
 */
export function ReportSummaryView({ summary }) {
  const { totals, days, weeks } = summary
  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        بازهٔ {formatDayCompact(summary.from)} تا {formatDayCompact(summary.to)} — روزها به وقت تهران
      </p>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Card label="کاربران فعال" value={numberFa(totals.activeUsers)} hint="دست‌کم یک تلاش در کل بازه" />
        <Card label="ثبت‌نام جدید" value={numberFa(totals.newSignups)} />
        <Card label="تلاش‌ها" value={numberFa(totals.attempts)} hint={`${numberFa(totals.scoredAttempts)} امتیازدهی‌شده · ${numberFa(totals.failedAttempts)} ناموفق`} />
        <Card label="نرخ خطا" value={percentFa(totals.failedRate)} />
        <Card label="میانگین امتیاز" value={numberFa(totals.averageScore)} hint="وزن‌دار با تعداد تلاش‌های امتیازدهی‌شده" />
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">به تفکیک روز</h3>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-500">
                <th className="px-4 py-2 text-start">روز</th>
                <th className="px-4 py-2 text-start">کاربران فعال (DAU)</th>
                <th className="px-4 py-2 text-start">ثبت‌نام</th>
                <th className="px-4 py-2 text-start">تلاش‌ها</th>
                <th className="px-4 py-2 text-start">نرخ خطا</th>
                <th className="px-4 py-2 text-start">میانگین امتیاز</th>
              </tr>
            </thead>
            <tbody>
              {days.map(d => (
                <tr key={d.date} className="border-t border-slate-50">
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-600">{formatDayCompact(d.date)}</td>
                  <td className="px-4 py-2 text-slate-700">{numberFa(d.activeUsers)}</td>
                  <td className="px-4 py-2 text-slate-700">{numberFa(d.newSignups)}</td>
                  <td className="px-4 py-2 text-slate-700">{numberFa(d.attempts)}</td>
                  <td className="px-4 py-2 text-slate-700">{percentFa(d.failedRate)}</td>
                  <td className="px-4 py-2 text-slate-700">{numberFa(d.averageScore)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">به تفکیک هفته (شنبه تا جمعه)</h3>
        {weeks.length === 0
          ? <p className="py-6 text-center text-sm text-slate-400">در این بازه هفته‌ای با فعالیت نبود.</p>
          : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-xs text-slate-500">
                    <th className="px-4 py-2 text-start">شروع هفته</th>
                    <th className="px-4 py-2 text-start">کاربران فعال هفته (WAU)</th>
                  </tr>
                </thead>
                <tbody>
                  {weeks.map(w => (
                    <tr key={w.weekStart} className="border-t border-slate-50">
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-600">{formatDayCompact(w.weekStart)}</td>
                      <td className="px-4 py-2 text-slate-700">{numberFa(w.activeUsers)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  )
}

/**
 * The reports tab (S3-p). The two inputs take `YYYY/MM/DD` (Persian or Latin
 * digits); the wire gets `YYYY-MM-DD`. Opens on the last
 * 30 days — the backend's own default, spelled out so the inputs show it.
 */
export function ReportsTab() {
  const [range] = useState(() => defaultRange())
  const [fromText, setFromText] = useState(() => isoToDateInput(range.from))
  const [toText, setToText] = useState(() => isoToDateInput(range.to))
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  const load = useCallback(async (params) => {
    setLoading(true); setErr('')
    try { setSummary(await getReportSummary(params)) }
    catch (ex) { setErr(errorMessage(ex)) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load(range) }, [load, range])

  function apply(e) {
    e.preventDefault()
    const from = dateInputToIso(fromText)
    const to = dateInputToIso(toText)
    if (!from || !to) { setErr('تاریخ را به شکل ۲۰۲۶/۰۹/۱۸ (میلادی) وارد کنید.'); return }
    if (from > to) { setErr('تاریخ شروع باید پیش از تاریخ پایان باشد.'); return }
    load({ from, to })
  }

  return (
    <div className="space-y-3">
      <form onSubmit={apply} className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">از تاریخ (میلادی)</span>
          <input type="text" value={fromText} onChange={e => setFromText(e.target.value)} dir="ltr" inputMode="numeric"
            placeholder="۲۰۲۶/۰۹/۰۱" className={`w-36 ${inputCls}`} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">تا تاریخ (میلادی)</span>
          <input type="text" value={toText} onChange={e => setToText(e.target.value)} dir="ltr" inputMode="numeric"
            placeholder="۲۰۲۶/۰۹/۳۰" className={`w-36 ${inputCls}`} />
        </label>
        <button type="submit" disabled={loading}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
          {loading ? '…' : 'نمایش گزارش'}
        </button>
        <span className="text-xs text-slate-400">حداکثر یک سال؛ پیش‌فرض ۳۰ روز گذشته</span>
      </form>

      {err && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>}

      {summary === null && !err && <p className="py-16 text-center text-sm text-slate-400">در حال بارگذاری…</p>}
      {summary && <ReportSummaryView summary={summary} />}
    </div>
  )
}
